import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ColumnDef } from "@tanstack/react-table";
import { TanStackDataTable } from "../components/TanStackDataTable";
import { RawDataTable } from "../components/RawDataTable";

interface ByHourProps {
  onLastUpdatedChange?: (lastUpdated: string) => void;
  filterState: any;
  onFilterChange: any;
}

export interface ByHourRef {
  refresh: () => void;
  getLastUpdated: () => string;
}

interface RowData {
  category: string;
  grandTotal: number;
  subRows?: RowData[];
  [key: string]: any; // Dynamic hour bucket columns
}

interface DrillDownData {
  hourBucket: string;
  category: string;
  filterType: string;
  data: any[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
}

const ByHour = forwardRef<ByHourRef, ByHourProps>(
  ({ onLastUpdatedChange }, ref) => {
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<string>("N/A");
    const [data, setData] = useState<RowData[]>([]);
    const [runtimeDates, setRuntimeDates] = useState<string[]>([]);
    const [selectedRuntimeDate, setSelectedRuntimeDate] = useState<string>("");
    const [timezones, setTimezones] = useState<string[]>([]);
    const [selectedTimezones, setSelectedTimezones] = useState<string[]>([]);
    const [callTypes, setCallTypes] = useState<string[]>([]);
    const [selectedCallTypes, setSelectedCallTypes] = useState<string[]>([]);
    const [planningAreas, setPlanningAreas] = useState<string[]>([]);
    const [selectedPlanningAreas, setSelectedPlanningAreas] = useState<
      string[]
    >([]);
    const [apptDates, setApptDates] = useState<string[]>([]);
    const [selectedApptDates, setSelectedApptDates] = useState<string[]>([]);
    const [hourBuckets, setHourBuckets] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Snapshot table state
    const [snapshotData, setSnapshotData] = useState<Record<string, any>[]>([]);
    const [snapshotColumns, setSnapshotColumns] = useState<string[]>([]);
    const [snapshotLoading, setSnapshotLoading] = useState(true);

    // Drill-down modal state
    const [drillDownOpen, setDrillDownOpen] = useState(false);
    const [drillDownData, setDrillDownData] = useState<DrillDownData>({
      hourBucket: "",
      category: "",
      filterType: "",
      data: [],
      loading: false,
      total: 0,
      page: 0,
      rowsPerPage: 50,
    });

    // Fetch available runtime dates
    const fetchRuntimeDates = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/runtime-dates");
        const result = await response.json();
        if (result.success && result.runtimeDates) {
          setRuntimeDates(result.runtimeDates);
          // Default to first (most recent) date
          if (result.runtimeDates.length > 0 && !selectedRuntimeDate) {
            setSelectedRuntimeDate(result.runtimeDates[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching runtime dates:", err);
      }
    }, [selectedRuntimeDate]);

    // Fetch available timezones
    const fetchTimezones = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/timezones");
        const result = await response.json();
        if (result.success && result.timezones) {
          setTimezones(result.timezones);
        }
      } catch (err) {
        console.error("Error fetching timezones:", err);
      }
    }, []);

    // Fetch available call types
    const fetchCallTypes = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/call-types");
        const result = await response.json();
        if (result.success && result.callTypes) {
          setCallTypes(result.callTypes);
        }
      } catch (err) {
        console.error("Error fetching call types:", err);
      }
    }, []);

    // Fetch available planning areas
    const fetchPlanningAreas = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/planning-areas");
        const result = await response.json();
        if (result.success && result.planningAreas) {
          setPlanningAreas(result.planningAreas);
        }
      } catch (err) {
        console.error("Error fetching planning areas:", err);
      }
    }, []);

    // Fetch available appointment dates
    const fetchApptDates = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/appt-dates");
        const result = await response.json();
        if (result.success && result.apptDates) {
          setApptDates(result.apptDates);
        }
      } catch (err) {
        console.error("Error fetching appointment dates:", err);
      }
    }, []);

    // Fetch snapshot data with optional runtime date filter
    const fetchSnapshotData = useCallback(async (runtimeDate?: string) => {
      try {
        setSnapshotLoading(true);
        const params = new URLSearchParams();
        if (runtimeDate) params.append("runtimeDate", runtimeDate);
        const queryString = params.toString() ? `?${params.toString()}` : "";
        const response = await fetch(`/api/snowflake/snapshot${queryString}`);
        const result = await response.json();
        if (result.success && result.data) {
          setSnapshotData(result.data);
          if (result.columns) {
            setSnapshotColumns(result.columns);
          }
        }
      } catch (err) {
        console.error("Error fetching snapshot data:", err);
      } finally {
        setSnapshotLoading(false);
      }
    }, []);

    // Fetch runtime dates on mount
    useEffect(() => {
      fetchRuntimeDates();
    }, [fetchRuntimeDates]);

    // Fetch filter options on mount
    useEffect(() => {
      fetchTimezones();
      fetchCallTypes();
      fetchPlanningAreas();
      fetchApptDates();
    }, [fetchTimezones, fetchCallTypes, fetchPlanningAreas, fetchApptDates]);

    // Fetch snapshot data when runtime date changes
    useEffect(() => {
      if (selectedRuntimeDate) {
        fetchSnapshotData(selectedRuntimeDate);
      }
    }, [selectedRuntimeDate, fetchSnapshotData]);

    // Get endpoint based on active tab
    const getEndpoint = useCallback(
      (
        tabIndex: number,
        tzs: string[],
        cts: string[],
        pas: string[],
        ads: string[],
        runtimeDate?: string,
      ) => {
        const params = new URLSearchParams();
        if (runtimeDate) params.append("runtimeDate", runtimeDate);
        if (tzs.length > 0) params.append("timezones", tzs.join(","));
        if (cts.length > 0) params.append("callTypes", cts.join(","));
        if (pas.length > 0) params.append("planningAreas", pas.join(","));
        if (ads.length > 0) params.append("apptDates", ads.join(","));
        const queryString = params.toString() ? `?${params.toString()}` : "";
        switch (tabIndex) {
          case 0:
            return `/api/snowflake/by-hour${queryString}`; // By Jeopardy Reason
          case 1:
            return `/api/snowflake/by-hour/payment-type${queryString}`; // By Payment Type
          case 2:
            return `/api/snowflake/by-hour/planning-area${queryString}`; // By Planning Area
          case 3:
            return `/api/snowflake/by-hour/timezone${queryString}`; // By Timezone
          case 4:
            return `/api/snowflake/by-hour/service-order${queryString}`; // By Service Order
          default:
            return `/api/snowflake/by-hour${queryString}`;
        }
      },
      [],
    );

    // Fetch aggregated data from API
    const fetchData = useCallback(
      async (isRefresh = false) => {
        try {
          setError(null);
          if (!isRefresh) {
            setLoading(true);
          }

          const endpoint = getEndpoint(
            activeSubTab,
            selectedTimezones,
            selectedCallTypes,
            selectedPlanningAreas,
            selectedApptDates,
            selectedRuntimeDate,
          );
          const response = await fetch(endpoint);
          const result = await response.json();

          if (result.success) {
            setData(result.data);
            setHourBuckets(result.hourBuckets || []);
            const currentTime = new Date().toLocaleString();
            setLastUpdated(currentTime);
            if (onLastUpdatedChange) {
              onLastUpdatedChange(currentTime);
            }
          } else {
            setError(result.error || "Failed to fetch data");
          }
        } catch (err: any) {
          setError(err.message || "Failed to fetch data");
          console.error("Error fetching by-hour data:", err);
        } finally {
          setLoading(false);
        }
      },
      [
        onLastUpdatedChange,
        activeSubTab,
        getEndpoint,
        selectedTimezones,
        selectedCallTypes,
        selectedPlanningAreas,
        selectedApptDates,
        selectedRuntimeDate,
      ],
    );

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    const handleSubTabChange = (
      _event: React.SyntheticEvent,
      newValue: number,
    ) => {
      setActiveSubTab(newValue);
    };

    // Get filter type based on active tab
    const getFilterType = useCallback((tabIndex: number) => {
      switch (tabIndex) {
        case 0:
          return "jeopardy";
        case 1:
          return "payment-type";
        case 2:
          return "planning-area";
        case 3:
          return "timezone";
        case 4:
          return "service-order";
        default:
          return "jeopardy";
      }
    }, []);

    // Fetch drill-down data with pagination
    const fetchDrillDownData = useCallback(
      async (
        hourBucket: string,
        category: string,
        filterType: string,
        page: number,
        rowsPerPage: number,
      ) => {
        setDrillDownData((prev) => ({ ...prev, loading: true }));

        try {
          const response = await fetch("/api/snowflake/by-hour/drill-down", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              hourBucket,
              category,
              filterType,
              runtimeDate: selectedRuntimeDate || null,
              timezones:
                selectedTimezones.length > 0 ? selectedTimezones : null,
              callTypes:
                selectedCallTypes.length > 0 ? selectedCallTypes : null,
              planningAreas:
                selectedPlanningAreas.length > 0 ? selectedPlanningAreas : null,
              apptDates:
                selectedApptDates.length > 0 ? selectedApptDates : null,
              page: page + 1, // API uses 1-based pagination
              limit: rowsPerPage,
            }),
          });
          const result = await response.json();

          if (result.success) {
            setDrillDownData((prev) => ({
              ...prev,
              data: result.data,
              total: result.pagination?.total || 0,
              loading: false,
            }));
          } else {
            console.error("Drill-down error:", result.error);
            setDrillDownData((prev) => ({ ...prev, loading: false }));
          }
        } catch (err) {
          console.error("Error fetching drill-down data:", err);
          setDrillDownData((prev) => ({ ...prev, loading: false }));
        }
      },
      [
        selectedRuntimeDate,
        selectedTimezones,
        selectedCallTypes,
        selectedPlanningAreas,
        selectedApptDates,
      ],
    );

    // Handle cell click for drill-down
    const handleCellClick = useCallback(
      (hourBucket: string, category: string) => {
        if (category === "Grand Total") return; // Don't drill down on grand total

        const filterType = getFilterType(activeSubTab);
        setDrillDownOpen(true);
        setDrillDownData({
          hourBucket,
          category,
          filterType,
          data: [],
          loading: true,
          total: 0,
          page: 0,
          rowsPerPage: 50,
        });

        fetchDrillDownData(hourBucket, category, filterType, 0, 50);
      },
      [activeSubTab, getFilterType, fetchDrillDownData],
    );

    // Handle drill-down pagination
    const handleDrillDownPageChange = (_event: unknown, newPage: number) => {
      setDrillDownData((prev) => ({ ...prev, page: newPage }));
      fetchDrillDownData(
        drillDownData.hourBucket,
        drillDownData.category,
        drillDownData.filterType,
        newPage,
        drillDownData.rowsPerPage,
      );
    };

    const handleDrillDownRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setDrillDownData((prev) => ({
        ...prev,
        rowsPerPage: newRowsPerPage,
        page: 0,
      }));
      fetchDrillDownData(
        drillDownData.hourBucket,
        drillDownData.category,
        drillDownData.filterType,
        0,
        newRowsPerPage,
      );
    };

    // Close drill-down modal
    const handleCloseDrillDown = () => {
      setDrillDownOpen(false);
      setDrillDownData({
        hourBucket: "",
        category: "",
        filterType: "",
        data: [],
        loading: false,
        total: 0,
        page: 0,
        rowsPerPage: 50,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          fetchData(true);
        },
        getLastUpdated: () => lastUpdated,
      }),
      [fetchData, lastUpdated],
    );

    // Dynamically generate column definitions based on hour buckets from API
    const columns = useMemo<ColumnDef<RowData>[]>(() => {
      const cols: ColumnDef<RowData>[] = [
        {
          accessorKey: "category",
          header: "Service Order",
          size: 200,
        },
      ];

      // Add dynamic hour bucket columns with clickable cells
      hourBuckets.forEach((hour) => {
        cols.push({
          accessorKey: hour,
          header: hour,
          size: 55,
          cell: ({ getValue, row }) => {
            const value = getValue<number>();
            const category = row.original.category;
            const isGrandTotal = category === "Grand Total";

            if (value === undefined || value === null || value === 0) {
              return "";
            }

            return (
              <Box
                component="span"
                onClick={() => !isGrandTotal && handleCellClick(hour, category)}
                sx={{
                  cursor: isGrandTotal ? "default" : "pointer",
                  color: isGrandTotal ? "inherit" : "#1976d2",
                  "&:hover": {
                    textDecoration: isGrandTotal ? "none" : "underline",
                  },
                }}
              >
                {value.toLocaleString()}
              </Box>
            );
          },
        });
      });

      return cols;
    }, [hourBuckets, handleCellClick]);

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: { xs: 2, md: 1.75 },
          bgcolor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Runtime Date Filter */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="runtime-date-filter-label">Run Date</InputLabel>
            <Select
              labelId="runtime-date-filter-label"
              id="runtime-date-filter"
              value={selectedRuntimeDate}
              label="Run Date"
              onChange={(e) => setSelectedRuntimeDate(e.target.value as string)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              {runtimeDates.map((rd) => (
                <MenuItem key={rd} value={rd} sx={{ fontSize: "0.8rem" }}>
                  {rd}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Snapshot Table */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Summary by Progression
          </Typography>
          {snapshotLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : snapshotData.length > 0 ? (
            <Box
              sx={{
                overflowX: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "#fff",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.75rem",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    {(snapshotColumns.length > 0
                      ? snapshotColumns
                      : Object.keys(snapshotData[0])
                    ).map((key) => (
                      <th
                        key={key}
                        style={{
                          padding: "6px 8px",
                          textAlign: "left",
                          borderBottom: "1px solid #e0e0e0",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshotData.map((row, idx) => (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                      }}
                    >
                      {(snapshotColumns.length > 0
                        ? snapshotColumns
                        : Object.keys(row)
                      ).map((col, colIdx) => (
                        <td
                          key={colIdx}
                          style={{
                            padding: "4px 8px",
                            borderBottom: "1px solid #f0f0f0",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row[col] !== null && row[col] !== undefined
                            ? typeof row[col] === "number"
                              ? row[col].toLocaleString()
                              : String(row[col])
                            : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : null}
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="timezone-filter-label">Timezone</InputLabel>
            <Select
              labelId="timezone-filter-label"
              id="timezone-filter"
              multiple
              value={selectedTimezones}
              label="Timezone"
              onChange={(e) =>
                setSelectedTimezones(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              renderValue={(selected) =>
                selected.length === 0 ? "All" : selected.join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTimezones([...timezones]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTimezones([]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Clear All
                </Button>
              </Box>
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz} sx={{ fontSize: "0.8rem" }}>
                  <Checkbox
                    checked={selectedTimezones.indexOf(tz) > -1}
                    size="small"
                  />
                  <ListItemText
                    primary={tz}
                    primaryTypographyProps={{ fontSize: "0.8rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="calltype-filter-label">Call Type</InputLabel>
            <Select
              labelId="calltype-filter-label"
              id="calltype-filter"
              multiple
              value={selectedCallTypes}
              label="Call Type"
              onChange={(e) =>
                setSelectedCallTypes(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              renderValue={(selected) =>
                selected.length === 0 ? "All" : selected.join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCallTypes([...callTypes]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCallTypes([]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Clear All
                </Button>
              </Box>
              {callTypes.map((ct) => (
                <MenuItem key={ct} value={ct} sx={{ fontSize: "0.8rem" }}>
                  <Checkbox
                    checked={selectedCallTypes.indexOf(ct) > -1}
                    size="small"
                  />
                  <ListItemText
                    primary={ct}
                    primaryTypographyProps={{ fontSize: "0.8rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="planningarea-filter-label">
              Planning Area
            </InputLabel>
            <Select
              labelId="planningarea-filter-label"
              id="planningarea-filter"
              multiple
              value={selectedPlanningAreas}
              label="Planning Area"
              onChange={(e) =>
                setSelectedPlanningAreas(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              renderValue={(selected) =>
                selected.length === 0 ? "All" : selected.join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlanningAreas([...planningAreas]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlanningAreas([]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Clear All
                </Button>
              </Box>
              {planningAreas.map((pa) => (
                <MenuItem key={pa} value={pa} sx={{ fontSize: "0.8rem" }}>
                  <Checkbox
                    checked={selectedPlanningAreas.indexOf(pa) > -1}
                    size="small"
                  />
                  <ListItemText
                    primary={pa}
                    primaryTypographyProps={{ fontSize: "0.8rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="apptdate-filter-label">Appt Date</InputLabel>
            <Select
              labelId="apptdate-filter-label"
              id="apptdate-filter"
              multiple
              value={selectedApptDates}
              label="Appt Date"
              onChange={(e) =>
                setSelectedApptDates(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              renderValue={(selected) =>
                selected.length === 0 ? "All" : selected.join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedApptDates([...apptDates]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedApptDates([]);
                  }}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Clear All
                </Button>
              </Box>
              {apptDates.map((ad) => (
                <MenuItem key={ad} value={ad} sx={{ fontSize: "0.8rem" }}>
                  <Checkbox
                    checked={selectedApptDates.indexOf(ad) > -1}
                    size="small"
                  />
                  <ListItemText
                    primary={ad}
                    primaryTypographyProps={{ fontSize: "0.8rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Sub-tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeSubTab}
            onChange={handleSubTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="By Jeopardy Reason" />
            <Tab label="By Payment Type" />
            <Tab label="By Planning Area" />
            <Tab label="By Timezone" />
            <Tab label="By Service Order" />
          </Tabs>
        </Box>

        {/* TanStack DataTable with expandable rows */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TanStackDataTable
            data={data}
            columns={columns}
            getSubRows={(row) => row.subRows}
            maxHeight="400px"
            defaultExpanded={false}
            enableVirtualization={true}
            rowHeight={22}
            pinFirstColumn={true}
            enableSorting={true}
            sortableColumns={["category"]}
          />
        )}

        {/* Raw Data Table - Extracted to separate component */}
        <RawDataTable
          columnWidths={{ ORDERNUM: 110 }}
          runtimeDate={selectedRuntimeDate}
          timezones={selectedTimezones}
          callTypes={selectedCallTypes}
          planningAreas={selectedPlanningAreas}
          apptDates={selectedApptDates}
        />

        {/* Drill-down Modal */}
        <Dialog
          open={drillDownOpen}
          onClose={handleCloseDrillDown}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "90vw",
              maxWidth: "1400px",
              maxHeight: "95vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e2e8f0",
              py: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 600 }}
              >
                Raw Data: {drillDownData.category}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ ml: 2, color: "#64748b" }}
              >
                {selectedRuntimeDate} | Hour: {drillDownData.hourBucket} |{" "}
                {drillDownData.total.toLocaleString()} records
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDrillDown} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              paddingTop: "4px !important",
              pb: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <RawDataTable
              externalData={drillDownData.data}
              externalLoading={drillDownData.loading}
              externalTotal={drillDownData.total}
              drillDownFilters={{
                hourBucket: drillDownData.hourBucket,
                category: drillDownData.category,
                filterType: drillDownData.filterType,
              }}
              title=""
              showDownload={true}
              showPagination={false}
              columnWidths={{ ORDERNUM: 110 }}
              maxHeight="calc(95vh - 180px)"
              runtimeDate={selectedRuntimeDate}
              timezones={selectedTimezones}
              callTypes={selectedCallTypes}
              planningAreas={selectedPlanningAreas}
              apptDates={selectedApptDates}
            />
            <TablePagination
              component="div"
              count={drillDownData.total}
              page={drillDownData.page}
              onPageChange={handleDrillDownPageChange}
              rowsPerPage={drillDownData.rowsPerPage}
              onRowsPerPageChange={handleDrillDownRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{
                borderTop: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  minHeight: 36,
                  height: 36,
                  overflow: "hidden",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: "0.75rem",
                  },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  },
);

ByHour.displayName = "ByHour";

export default ByHour;
